import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, money, stageLabel } from "./api";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl bg-white p-5" data-testid={testid}>
    <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</p>
    <p className="font-display mt-2 text-4xl font-black">{value}</p>
  </div>
);

const Bars = ({ title, rows, label = (r) => r.name, testid }) => {
  const max = Math.max(1, ...rows.map((r) => r.leads ?? r.count ?? 0));
  return (
    <div className="rounded-2xl bg-white p-5" data-testid={testid}>
      <h2 className="font-display text-2xl font-black uppercase">{title}</h2>
      {rows.length === 0 && <p className="mt-3 text-sm text-slate-500">No data yet.</p>}
      <ul className="mt-4 space-y-3">
        {rows.map((r) => {
          const n = r.leads ?? r.count ?? 0;
          return (
            <li key={label(r)}>
              <div className="flex justify-between gap-3 text-sm"><span className="truncate font-semibold">{label(r)}</span>
                <span className="shrink-0 text-slate-600">{n} lead{n === 1 ? "" : "s"}{r.won != null ? ` · ${r.won} won · ${money(r.revenue)}` : ""}</span></div>
              <div className="mt-1 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-slate-900" style={{ width: `${(100 * n) / max}%` }} /></div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default function Numbers() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => setS({})); }, []);
  if (!s) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-slate-400" />;
  const month = (m) => (m ? new Date(`${m}-01T12:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "Unknown");
  return (
    <div data-testid="admin-numbers">
      <h1 className="font-display text-4xl font-black uppercase">Marketing numbers</h1>
      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat label="Total leads" value={s.total_leads ?? 0} testid="stat-total-leads" />
        <Stat label="Open leads" value={s.open_leads ?? 0} testid="stat-open-leads" />
        <Stat label="Jobs won" value={s.won_jobs ?? 0} testid="stat-won-jobs" />
        <Stat label="Won revenue" value={money(s.revenue ?? 0)} testid="stat-revenue" />
        <Stat label="Close rate" value={s.close_rate == null ? "—" : `${s.close_rate}%`} testid="stat-close-rate" />
      </div>
      <p className="mt-2 text-sm text-slate-500">Estimates out right now: <b>{money(s.pipeline_value ?? 0)}</b>. Close rate = won ÷ (won + lost).</p>
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Bars title="Sales stages" rows={(s.by_stage ?? []).map((r) => ({ ...r, name: stageLabel(r.stage) }))} testid="chart-stages" />
        <Bars title="By month" rows={[...(s.by_month ?? [])].reverse()} label={(r) => month(r.month)} testid="chart-months" />
        <Bars title="By town" rows={s.by_town ?? []} testid="chart-towns" />
        <Bars title="By service" rows={s.by_service ?? []} testid="chart-services" />
        <Bars title="Page the lead came from" rows={s.by_page ?? []} testid="chart-pages" />
      </div>
    </div>
  );
}
